import React, { useId, useState } from 'react';
import './BaseInput.css';

type Props = {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  type?: string;
  readOnly?: boolean;
  maxLength?: number;
  required?: boolean;
  error?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
};

const BaseInput: React.FC<Props> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  type = 'text',
  readOnly = false,
  maxLength,
  required = false,
  error,
  autoFocus = false,
  autoComplete = 'off',
  disabled = false,
  inputMode,
}) => {
  const uid = useId();
  const inputId = `${name}-${uid}`;
  const [focused, setFocused] = useState(false);
  const hasValue = Boolean(String(value ?? '').length);
  const floated = focused || hasValue;

  const handleFocus = () => setFocused(true);
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur?.(e);
  };

  return (
    <div
      className={[
        'base-input',
        floated ? 'base-input--floated' : '',
        focused ? 'base-input--focused' : '',
        error ? 'base-input--error' : '',
        disabled || readOnly ? 'base-input--locked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="base-input__field">
        <input
          type={type}
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLength}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          inputMode={inputMode}
          placeholder=" "
          className="base-input__control form-control"
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          aria-readonly={readOnly || undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
        <label htmlFor={inputId} className="base-input__label">
          {label}
          {required ? <span className="base-input__required" aria-hidden> *</span> : null}
        </label>
      </div>
      {error ? (
        <div id={`${inputId}-error`} className="base-input__error form-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default BaseInput;
